import { DirectionId } from 'components/Modals/ChooseProductModal/components/LifetimeProducts/types';
import { useLottieJsonWithStatus } from 'hooks/useLottieJson';
import Lottie from 'lottie-react';
import React, { Dispatch, FC, SetStateAction } from 'react';
import { dayjs } from 'services';
import { selectors, useAppSelector } from 'store';
import { PaymentOptions } from 'store/ducks/courses/types';
import styled from 'styled-components';

import { getCourseSpecificGiftAnimation, giftAnimatedMapping } from './constants';

interface BonusSectionProps {
  setIsShowBonusModal: Dispatch<SetStateAction<boolean>>;
  isProduct: boolean;
  isCertificate?: boolean;
  course?: any;
}

export const BonusSection: FC<BonusSectionProps> = ({ setIsShowBonusModal, isProduct, isCertificate, course }) => {
  const direction = useAppSelector(selectors.direction.selectDirection);
  const isDarkMode = false;
  const paymentOption = useAppSelector(selectors.courses.selectPaymentOption);

  const directionId = direction?.id || '';

  const isPilotCarer =
    directionId === '451553b1-e21e-4221-b248-e160177536e6x' || directionId === '01f94266-8371-42d6-bc52-961a53dc65e0';
  const today = dayjs();
  const day = today.date();

  const getOrdinal = (day: number) => {
    if (day % 10 === 1 && day % 100 !== 11) return 'st';
    if (day % 10 === 2 && day % 100 !== 12) return 'nd';
    if (day % 10 === 3 && day % 100 !== 13) return 'rd';
    return 'th';
  };

  // Get course-specific gift animation, fallback to direction-based animation
  const bonusesUrlRaw = typeof course?.pricingAnimationBonusesUrl === 'string' ? course.pricingAnimationBonusesUrl : '';
  const bonusesUrl = bonusesUrlRaw.trim();
  const { data: bonusesUrlData, status: bonusesStatus } = useLottieJsonWithStatus(bonusesUrl);
  const courseSpecificGiftAnimation = getCourseSpecificGiftAnimation(course, isDarkMode);
  const giftAnimation = bonusesUrl
    ? bonusesStatus === 'loaded'
      ? bonusesUrlData
      : bonusesStatus === 'error'
      ? courseSpecificGiftAnimation || giftAnimatedMapping[directionId as DirectionId]
      : null
    : courseSpecificGiftAnimation || giftAnimatedMapping[directionId as DirectionId];

  const lottieKey = bonusesUrl ? `bonus-url:${bonusesUrl}:${bonusesStatus}` : 'bonus-default';

  // Check if this is installment payment type for extra padding
  const isInstallmentPayment = paymentOption === PaymentOptions.PAYMENT_PLAN;

  // Check if this is a bundle course for gradient styling (matching backend logic)
  const isBundleCourse = course?.type === 'bundle' || course?.type?.toLowerCase() === 'bundle';

  const overrideColorRaw = typeof course?.upsellPrimaryColor === 'string' ? course.upsellPrimaryColor.trim() : '';
  const overrideColor =
    overrideColorRaw && !overrideColorRaw.includes('linear-gradient') ? overrideColorRaw : undefined;

  return (
    <>
      <BonusContainer
        isProduct={isProduct}
        isPilotCarer={isPilotCarer}
        isCertificate={isCertificate}
        isInstallmentPayment={isInstallmentPayment}
        onClick={() => setIsShowBonusModal(true)}>
        {giftAnimation ? (
          <Lottie
            key={lottieKey}
            animationData={giftAnimation}
            style={{
              width: '50px',
              height: '50px',
            }}
          />
        ) : (
          <div style={{ width: '50px', height: '50px' }} />
        )}
        <div>
          <BonusTitle isBundle={isBundleCourse} mainColor={overrideColor}>
            Click to see MVP bonuses
          </BonusTitle>
          <BonusTitle
            isBundle={isBundleCourse}
            mainColor={overrideColor}
            style={{ minWidth: '70px', maxWidth: '120px' }}>
            {`for ${today.format('MMMM ')}${day}${getOrdinal(day)} `}
          </BonusTitle>
        </div>
      </BonusContainer>
    </>
  );
};

const BonusContainer = styled.div<{
  isProduct: boolean;
  isPilotCarer: boolean;
  isCertificate: boolean | undefined;
  isInstallmentPayment: boolean;
}>`
  width: 250px;
  display: flex;
  margin: ${({ isProduct }) => (isProduct ? 'unset' : '20px 0')};
  justify-content: center;
  align-items: center;
  top: ${({ isProduct, isCertificate, isInstallmentPayment }) => {
    if (!isProduct) return 'unset';
    if (isInstallmentPayment) {
      return isCertificate ? '580px' : '570px'; // More padding for installment payment
    }
    return isCertificate ? '580px' : '530px';
  }};
  left: ${({ isProduct }) => (isProduct ? '35px' : 'unset')};
  position: ${({ isProduct }) => (isProduct ? 'absolute' : 'unset')};
  cursor: pointer;
`;

const BonusTitle = styled.p<{ isBundle?: boolean; mainColor?: string }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ mainColor, isBundle, theme: { colors } }) => {
    if (mainColor) return mainColor;
    if (isBundle) return '#ED5A26'; // Orange from the gradient
    return colors.primary[1];
  }};
  border-bottom: 1px dashed
    ${({ mainColor, isBundle, theme: { colors } }) => {
      if (mainColor) return mainColor;
      if (isBundle) return '#ED5A26'; // Orange from the gradient
      return colors.primary[1];
    }};
`;
